import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const FFT_SIZE = 256;
const BAR_SPACING = 2.5;
const BAR_CORNER_RADIUS = 100;
const HEIGHT_SCALE_FACTOR = 0.9;

const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  const h = Math.max(0, height);
  const r = Math.min(radius, width / 2, h / 2);

  if (width < 2 * r || h < 2 * r) {
    if (h > 0) ctx.fillRect(x, y, width, h);
    return;
  }

  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + h, r);
  ctx.arcTo(x + width, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
  ctx.fill();
};

const WaveformVisualization = ({ micStream, isRecording }) => {
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const waveformCanvasCtx = canvas.getContext('2d');

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      const { width, height } = entry.contentRect;
      const dpr = window.devicePixelRatio || 1;

      canvas.width = width * dpr;
      canvas.height = height * dpr;

      waveformCanvasCtx.scale(dpr, dpr);
    });

    resizeObserver.observe(canvas);

    const startVisualization = () => {
      if (!micStream || !isRecording) return;

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }
      const audioContext = audioContextRef.current;

      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      sourceRef.current = audioContext.createMediaStreamSource(micStream);
      analyserRef.current = audioContext.createAnalyser();

      analyserRef.current.fftSize = FFT_SIZE;
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      sourceRef.current.connect(analyserRef.current);

      const draw = () => {
        animationFrameIdRef.current = requestAnimationFrame(draw);

        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);

        const displayWidth = canvas.clientWidth;
        const displayHeight = canvas.clientHeight;

        waveformCanvasCtx.clearRect(0, 0, displayWidth, displayHeight);

        const gradient = waveformCanvasCtx.createLinearGradient(
          0,
          0,
          0,
          displayHeight
        );
        gradient.addColorStop(1, '#023ce4');
        gradient.addColorStop(0.75, '#0863ed');
        gradient.addColorStop(0.2, '#00D4FF');
        waveformCanvasCtx.fillStyle = gradient;

        const barWidth = (displayWidth / bufferLength) * 1.5;
        let x = 0;

        for (let i = 0; i < bufferLength; i++) {
          const barHeight =
            (dataArray[i] / 255) * displayHeight * HEIGHT_SCALE_FACTOR;
          drawRoundedRect(
            waveformCanvasCtx,
            x,
            displayHeight - barHeight,
            barWidth,
            barHeight,
            BAR_CORNER_RADIUS
          );
          x += barWidth + BAR_SPACING;
        }
      };

      draw();
    };

    const stopVisualization = () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }

      const displayWidth = canvas.clientWidth;
      const displayHeight = canvas.clientHeight;
      waveformCanvasCtx.clearRect(0, 0, displayWidth, displayHeight);
    };

    if (isRecording && micStream) {
      startVisualization();
    } else {
      stopVisualization();
    }

    return () => {
      resizeObserver.disconnect();
      stopVisualization();

      if (
        audioContextRef.current &&
        audioContextRef.current.state !== 'closed'
      ) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, [micStream, isRecording]);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="29px"
      width="100%"
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
    </Box>
  );
};

export default WaveformVisualization;
